// @flow
import React, { useState, useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { MdArrowBack, MdInfoOutline } from 'react-icons/md';
import Button from 'components/common/Button';
import { imageURL, numberWithCommas, parseArrayString } from 'lib/common';
import { camelCase, kebabCase, get } from 'lodash';
import { actionCreators as shippingAddressesActions } from 'store/modules/shippingAddresses';
import { actionCreators as userActions } from 'store/modules/user';
import './CheckoutTemplate.scss';

type Props = {
  logged: boolean,
  cart: any,
  handleChange: any,
  onCheckOut: any,
  checkoutTerms: boolean,
  checkoutPrivacy: boolean,
  checkoutAll: boolean,
  onCheckoutTerms: any,
  onCheckoutPrivacy: any,
  onCheckoutAll: any,
  onCartCoupon: any,
  applyCoupons: any,
  applyDiscounts: any,
  appliedDiscount: string,
  onPaymentMethod: any,
  addressNameFull: string,
  addressEmail: string,
  addressMobile: string,
  addressPhone: string,
  addressState: string,
  addressCity: string,
  addressAddress1: string,
  addressAddress2: string,
  addressPostCode: string,
  request: string,
  isAuthenticated: boolean,
  // 주문자 정보 라디오 버튼 관련
  ordererInfoType: string,
  onOrdererInfoTypeChange: any,
  onUseMyInfo: any,
  onChangeOrdererInfo: any,
  // 배송지 정보 라디오 버튼 관련
  shippingInfoType: string,
  onShippingInfoTypeChange: any,
  onUseRecentAddress: any,
  onUseNewAddress: any,
  // Redux 관련 props
  shippingAddresses: any,
  ShippingAddressesActions: any,
  UserActions: any,
  // 발급수단 관련
  selectedPaymentType: string,
  documentaryEvidenceInfo: {
    documentType: string,
    preIssuanceAgreement: boolean,
    name: string,
    contact: string,
    sub_contact: string,
    email: string,
    business_no: string,
    tax_registration_id: string,
    business_item: string,
    business_type: string,
    company_name: string,
    representative_name: string,
    business_address: string,
  },
  onDocumentTypeChange: any,
  onDocumentInfoChange: any,
  onDocumentInfoBulkUpdate: any,
  // Redux 관련
  taxInvoiceList: any,
  TaxInvoiceActions: any,
};

const translationKeys = {
  'name.first': 'firstName',
  'name.last': 'lastName',
  'name.full': 'fullName',
};

const toFieldDetail = (prefix = '') => field => {
  const [key, required] = field.split(':');
  return {
    required: required === 'required',
    key,
    translationKey: camelCase([prefix, translationKeys[key] || key]),
  };
};

// 배송메모 드롭다운 컴포넌트
const ShippingMemoDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState('');
  const [customInput, setCustomInput] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  const shippingOptions = [
    '경비실에 맡겨주세요',
    '배송전 연락주세요',
    '택배함에 맡겨주세요',
    '직접 입력',
  ];

  const handleOptionSelect = option => {
    if (option === '직접 입력') {
      setShowCustomInput(true);
      setSelectedOption(option);
      setCustomInput(''); // 직접 입력 선택 시 인풀창 초기화
      // 빈 값으로 onChange 호출
      const event = {
        target: {
          name: 'request',
          value: '',
        },
      };
      onChange(event);
    } else {
      setShowCustomInput(false);
      setSelectedOption(option);
      setCustomInput('');
      // props.handleChange 호출
      const event = {
        target: {
          name: 'request',
          value: option,
        },
      };
      onChange(event);
    }
    setIsOpen(false);
  };

  const handleCustomInputChange = e => {
    setCustomInput(e.target.value);
    const event = {
      target: {
        name: 'request',
        value: e.target.value,
      },
    };
    onChange(event);
  };

  const getDisplayValue = () => {
    if (selectedOption === '직접 입력') {
      return '직접 입력';
    }
    if (selectedOption) {
      return selectedOption;
    }
    if (value && !shippingOptions.includes(value)) {
      return value;
    }
    return '배송 시 요청 사항을 선택해 주세요.';
  };

  return (
    <div className="shipping-memo">
      <label>
        <span className="shipping-memo-label">배송메모</span>
        <div className="shipping-memo-dropdown">
          <div
            className={`dropdown-header ${isOpen ? 'open' : ''}`}
            onClick={() => setIsOpen(!isOpen)}
          >
            <span className="dropdown-text">{getDisplayValue()}</span>
            <span className={`dropdown-arrow ${isOpen ? 'up' : 'down'}`}>▼</span>
          </div>

          {isOpen && (
            <div className="dropdown-options">
              {shippingOptions.map(option => (
                <div
                  key={option}
                  className={`dropdown-option ${selectedOption === option ? 'selected' : ''}`}
                  onClick={() => handleOptionSelect(option)}
                >
                  {option}
                </div>
              ))}
            </div>
          )}

          {showCustomInput && (
            <div className="custom-input-container">
              <textarea
                className="custom-input"
                placeholder="배송 메모를 입력해주세요"
                value={customInput}
                onChange={handleCustomInputChange}
                maxLength={100}
              />
              <div className="character-count">{customInput.length} / 100</div>
            </div>
          )}
        </div>
      </label>
    </div>
  );
};

const paymentMethods = [
  // {
  //   id: 'naverpay',
  //   label: 'naverPay',
  //   value: '네이버페이',
  //   cardFields: false,
  //   payLater: false,
  // },
  {
    id: 'tosspayments',
    label: 'tosspayments',
    cardFields: false,
    payLater: false,
    meta: {
      pg: 'tosspayments',
      payMethod: 'card',
    },
  },
  {
    id: 'iamport',
    label: 'niceByCard',
    cardFields: false,
    payLater: false,
    meta: {
      pg: 'nice',
      payMethod: 'card',
    },
  },
  {
    id: 'virtual-bank',
    label: 'virtualBank',
    cardFields: false,
    payLater: false,
    meta: {
      pg: 'nice',
      payMethod: 'vbank',
    },
  },
  {
    id: 'research-transfer',
    label: 'researchTransfer',
    cardFields: false,
    payLater: true,
  },
  {
    id: 'kb-bank-transfer',
    label: 'kbBankTransfer',
    cardFields: false,
    payLater: true,
  },
  {
    id: 'bank-transfer',
    label: 'bankTransfer',
    cardFields: false,
    payLater: true,
  },
  {
    id: 'post-transfer',
    label: 'postTransfer',
    cardFields: false,
    payLater: true,
  },
  {
    id: 'quick-bank-transfer',
    label: 'quickBankTransfer',
    cardFields: false,
    payLater: true,
  },
];

const customerFields = parseArrayString([
  'name.full:required',
  'email:required',
  'mobile:required',
  'phone',
]).map(toFieldDetail('customer'));

const recipientFields = parseArrayString([
  'name.full:required',
  'email:required',
  'mobile:required',
  'phone',
]).map(toFieldDetail('address'));

const statusTrans = key => {
  let words = {
    checkoutPreparationFailed: '주문에 필요한 정보를 가져오는데 실패했습니다.',
    invalidCartItemIncluded: '주문할 수 없는 상품이 포함되어 있습니다. 구매 상품을 리뷰해 주세요.',
    invalidPostcode: '지원되지 않는 우편번호 형식입니다.',
    startsTooEarly: '정기 구독 시작 일시가 너무 이릅니다.',
    cartCouponCategory: '장바구니 쿠폰이 적용되는 상품이 하나도 없습니다.',
    cartCouponPrice: '장바구니 쿠폰을 적용할 수 없는 주문 총액입니다.',
    cartUpdateFailed: '장바구니에 변경 사항을 반영하는데 실패했습니다.',
    confirmItems: '주문서',
    backToCart: '장바구니로 돌아가기',
    itemInfo: '상품 정보',
    itemQuantity: '수량',
    itemPrice: '가격',
    itemOption: '상품 옵션',
    shippingMethod: '배송 방식',
    appliedCoupon: '적용 쿠폰',
    selectProductCoupon: '상품 쿠폰 선택',
    selectCartCoupon: '장바구니 쿠폰 선택',
    alreadyDiscounted: '이미 할인된 경우 적용할 수 없는 쿠폰입니다.',
    mustBeOneQuantity: '담은 수량이 1개인 품목에만 적용할 수 있습니다.',
    duplicatedCoupon: '동일한 쿠폰을 여러품목에 적용할 수 없습니다.',
    applyCoupons: '쿠폰 적용',
    finishApplyingCoupons: '쿠폰 적용 완료',
    selectSubscriptionPlanFirst: '정기 주문 방식을 먼저 선택해야합니다.',
    required: '필수',
    setCustomer: '주문자 정보',
    customerFirstName: '주문자 이름',
    customerLastName: '주문자 성',
    customerFullName: '주문자',
    customerEmail: '이메일',
    customerMobile: '연락처',
    customerPhone: '사업자등록번호',
    goToUpdateCustomer: '내 기본 정보 변경',
    sameForRecipient: '주문자 정보도 위와 동일합니다.',
    customerFirstNameRequired: '주문자 이름을 입력해야합니다.',
    customerLastNameRequired: '주문자 성을 입력해야합니다.',
    customerFullNameRequired: '주문자를 입력해야합니다.',
    customerEmailRequired: '이메일을 입력해야합니다.',
    customerMobileRequired: '연락처를 입력해야합니다.',
    customerPhoneRequired: '사업자등록번호를 입력해야합니다.',
    setShippingAddress: '배송지 정보',
    recipientInfo: '주문자 정보',
    addressFirstName: '주문자 이름',
    addressLastName: '주문자 성',
    addressFullName: '주문자',
    addressEmail: '이메일',
    addressMobile: '연락처',
    addressPhone: '사업자등록번호',
    addressCountry: '국가',
    addressState: '도',
    addressCity: '시',
    addressAddress1: '주소',
    addressAddress2: '상세주소',
    addressPostcode: '우편번호',
    searchAddress: '주소 찾기',
    saveAsPrimaryAddress: '이 주소를 기본 주소로 저장',
    applyCouponsFirst: '쿠폰 적용을 먼저 끝내야합니다.',
    applyAddress: '설정된 주소 적용',
    doOrder: '주문하기',
    doSubscription: '정기 주문하기',
    addressFirstNameRequired: '주문자 이름을 입력해야합니다.',
    addressLastNameRequired: '주문자 성을 입력해야합니다.',
    addressFullNameRequired: '주문자를 입력해야합니다.',
    addressMobileRequired: '무선 연락처를 입력해야합니다.',
    addressPhoneRequired: '유선 연락처를 입력해야합니다.',
    addressCountryRequired: '배송지 정보의 국가를 입력해야합니다.',
    addressStateRequired: '배송지 정보의 도를 입력해야합니다.',
    addressCityRequired: '배송지 정보의 시를 입력해야합니다.',
    addressAddress1Required: '배송지 정보의 주소를 입력해야합니다.',
    addressAddress2Required: '배송지 정보의 상세 주소를 입력해야합니다.',
    addressPostcodeRequired: '배송지 정보의 우편번호를 입력해야합니다.',
    orderRequest: '요청 사항',
    typeOrderRequest: '요청 사항을 입력해주세요.',
    subscription: '정기 주문',
    subscriptionPlan: '정기 주문 방식',
    subscriptionPlanRequired: '정기 주문 방식을 선택해주세요.',
    selectSubscriptionPlan: '정기 주문 방식 선택',
    firstOrderStartsAt: '첫 주문일',
    secondOrderStartsAt: '두번째 주문일',
    subscriptionStartsAtRequired: '주문 시작일을 선택해주세요.',
    selectSubscriptionStartsAt: '주문 시작일 선택',
    firstOrderIsImmediate: '첫 주문은 즉시 주문됩니다.',
    payment: '결제',
    orderSummary: '주문 요약',
    subscriptionSummary: '정기 주문 요약',
    firstOrderSummary: '첫 주문',
    firstNowOrder: '즉시 주문',
    restOrdersSummary: '이후 주문',
    itemTotal: '상품 합계',
    shippingTotal: '배송비 합계',
    taxTotal: '부가세',
    orderTotal: '총계',
    agreeWithTerms: '이용 약관에 동의합니다.',
    viewTerms: '이용 약관 보기',
    agreeWithPrivacy: '개인정보 수집·이용에 동의합니다.',
    viewPrivacy: '개인정보 수집·이용 보기',
    agreeWithAll: '전체 동의',
    termsAgreementRequired: '이용 약관에 동의해야합니다.',
    privacyAgreementRequired: '개인정보 수집·이용에 동의해야합니다.',
    taxIncludedTip: '부가세는 가격에 포함 되어있습니다.',
    taxExcludedTip: '부가세는 가격에 미포함 되어있습니다.',
    shippingFeeAndTaxTip: '배송비와 부가세는 배송지 정보 설정 후에 계산됩니다.',
    proceedOrder: '주문 및 결제하기',
    proceedSubscription: '정기 주문 및 결제하기',
    checkoutFailed: '주문에 실패했습니다.',
    checkoutProcessFailed: '주문 절차 진행에 실패했습니다.',
    paymentFailed: '결제에 실패했습니다. 결제를 완료해주세요.',
    scheduleFailed: '정기 주문 예약에 실패했습니다.',
    paymentMethod: '결제 방식',
    cardNumber: '카드 번호',
    isCompanyCard: '법인 카드',
    cardExpiration: '만료일',
    cardPassword2: '비밀번호 첫 2자리',
    cardOwnerBirthdate: '생년월일',
    cardOwnerCompanyNumber: '사업자등록번호',
    selectPaymentMethod: '결제 방식을 선택해주세요.',
    paymentMethodRequired: '결제 방식을 선택해주세요.',
    cardNumberRequired: '카드 번호를 입력해주세요.',
    cardExpirationMonthRequired: '카드 만료 월을 입력해주세요.',
    cardExpirationYearRequired: '카드 만료 년도를 입력해주세요.',
    cardPassword2Required: '카드 비밀번호 첫 2자리를 입력해주세요.',
    cardOwnerRequired: '생년월일 또는 사업자등록번호를 입력해주세요.',
    // naverPay: '네이버페이',
    niceByCard: '신용카드',
    virtualBank: '가상계좌',
    researchTransfer: '연구비카드',
    kbBankTransfer: '세금계산서 (신한은행 100-032-703829)',
    bankTransfer: '세금계산서 (기업은행 033-502993-01-019)',
  };
  return words[key];
};

// 연락처 유효성 검증 함수
const isValidPhoneNumber = value => {
  // 숫자만 추출
  const numbers = value.replace(/[^\d]/g, '');

  // 최소 9자리 이상이어야 함 (02-123-4567 형태)
  if (numbers.length < 9) {
    return false;
  }

  // 휴대폰 번호는 11자리
  if (/^(010|011|016|017|018|019)/.test(numbers)) {
    return numbers.length === 11;
  }

  // 서울 지역번호는 9~10자리
  if (numbers.startsWith('02')) {
    return numbers.length >= 9 && numbers.length <= 10;
  }

  // 기타 지역번호는 10~11자리
  if (/^(03[1-9]|04[1-4]|05[1-5]|06[1-4])/.test(numbers)) {
    return numbers.length >= 10 && numbers.length <= 11;
  }

  // 기본적으로 9~11자리 허용
  return numbers.length >= 9 && numbers.length <= 11;
};

// 연락처 자동 하이픈 추가 함수
const formatPhoneNumber = value => {
  // 숫자만 추출
  const numbers = value.replace(/[^\d]/g, '');

  // 길이에 따라 포맷팅
  if (numbers.length <= 2) {
    return numbers;
  }

  // 지역번호 패턴 확인 (02, 031, 032, 033, 041, 042, 043, 044, 051, 052, 053, 054, 055, 061, 062, 063, 064)
  if (numbers.startsWith('02')) {
    // 서울 지역번호 (02-XXX-XXXX 또는 02-XXXX-XXXX)
    if (numbers.length <= 2) {
      return numbers;
    }
    if (numbers.length <= 5) {
      return `${numbers.slice(0, 2)}-${numbers.slice(2)}`;
    }
    if (numbers.length <= 9) {
      return `${numbers.slice(0, 2)}-${numbers.slice(2, 5)}-${numbers.slice(5)}`;
    }
    if (numbers.length <= 10) {
      return `${numbers.slice(0, 2)}-${numbers.slice(2, 6)}-${numbers.slice(6)}`;
    }
    // 10자리 초과시 10자리까지만 사용
    return `${numbers.slice(0, 2)}-${numbers.slice(2, 6)}-${numbers.slice(6, 10)}`;
  }

  // 기타 지역번호 (03X, 04X, 05X, 06X - XXX-XXX-XXXX 또는 XXX-XXXX-XXXX)
  if (/^(03[1-9]|04[1-4]|05[1-5]|06[1-4])/.test(numbers)) {
    if (numbers.length <= 3) {
      return numbers;
    }
    if (numbers.length <= 6) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    }
    if (numbers.length <= 10) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}`;
    }
    if (numbers.length <= 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    }
    // 11자리 초과시 11자리까지만 사용
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  }

  // 휴대폰 번호 (010, 011, 016, 017, 018, 019)
  if (/^(010|011|016|017|018|019)/.test(numbers)) {
    if (numbers.length <= 3) {
      return numbers;
    }
    if (numbers.length <= 7) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    }
    if (numbers.length <= 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    }
    // 11자리 초과시 11자리까지만 사용
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  }

  // 기본 패턴 (알 수 없는 번호)
  if (numbers.length <= 3) {
    return numbers;
  }
  if (numbers.length <= 7) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  }
  if (numbers.length <= 11) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
  }
  // 11자리 초과시 11자리까지만 사용
  return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
};

// 사업자등록번호 자동 하이픈 추가 함수 (xxx-xx-xxxxx)
const formatBusinessNumber = value => {
  // 숫자만 추출
  const numbers = value.replace(/[^\d]/g, '');

  // 길이에 따라 포맷팅
  if (numbers.length <= 3) {
    return numbers;
  }
  if (numbers.length <= 5) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  }
  if (numbers.length <= 10) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5)}`;
  }
  // 10자리 초과시 10자리까지만 사용
  return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5, 10)}`;
};

const CheckoutTemplate = (props: Props) => {
  // props에서 필요한 값 추출
  const { handleChange, documentaryEvidenceInfo } = props;

  // 배송지 관리 상태
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressList, setAddressList] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [isAddingNewAddress, setIsAddingNewAddress] = useState(false);

  // 발급목록 관리 상태
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [isEditingDocument, setIsEditingDocument] = useState(false);
  const [isAddingNewDocument, setIsAddingNewDocument] = useState(false);

  // Redux에서 세금계산서 목록 가져오기
  const taxInvoiceList = props.taxInvoiceList || [];
  const [formData, setFormData] = useState({
    label: '',
    name: '',
    mobile: '',
    phone: '',
    postcode: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    country: '',
    company: '',
  });

  // 배송지 목록 로드
  const loadShippingAddresses = async () => {
    if (!props.isAuthenticated) return;
    const response = await props.ShippingAddressesActions.getShippingAddresses();
    setAddressList(response.data.shippingAddressList);
  };

  // 세금계산서 발급목록 로드
  const loadTaxInvoices = async () => {
    if (!props.isAuthenticated) return;
    await props.TaxInvoiceActions.getTaxInvoiceList();
  };

  // 유저 정보 로드
  const loadUserInfo = async () => {
    if (!props.isAuthenticated) return;
    await props.UserActions.checkUser();
  };

  // 컴포넌트 마운트 시 유저 정보 및 배송지 목록 로드
  useEffect(() => {
    loadUserInfo();
    loadShippingAddresses();
  }, [props.isAuthenticated]);

  // 배송지 선택 핸들러
  const handleSelectAddress = address => {
    setSelectedAddress(address);

    // 선택한 배송지 정보를 메인 폼에 반영
    const addressData = {
      addressShippingNameFull: address.name,
      addressShippingMobile: address.mobile,
      // addressPhone: address.phone || '',
      addressPostCode: address.postcode,
      addressAddress1: address.address1,
      addressAddress2: address.address2 || '',
      addressCity: address.city || '',
      addressState: address.state || '',
    };

    // props의 handleChange를 통해 각 필드 업데이트
    Object.entries(addressData).forEach(([key, value]) => {
      const event = {
        target: {
          name: key,
          value,
        },
      };
      props.handleChange(event);
    });

    setShowAddressModal(false);
  };

  // 배송지 수정 핸들러
  const handleEditAddress = address => {
    setIsEditingAddress(true);
    setEditingAddressId(address.id);
    setFormData({
      label: address.label || '',
      name: address.name || '',
      mobile: address.mobile || '',
      phone: address.phone || '',
      postcode: address.postcode || '',
      address1: address.address1 || '',
      address2: address.address2 || '',
      city: address.city || '',
      state: address.state || '',
      country: address.country || '',
      company: address.company || '',
    });
  };

  // 배송지 삭제 핸들러
  const handleDeleteAddress = async addressId => {
    if (window.confirm('이 배송지를 삭제하시겠습니까?')) {
      try {
        await props.ShippingAddressesActions.deleteShippingAddress(addressId);
        // 삭제 후 목록 새로고침
        loadShippingAddresses();
      } catch (error) {
        alert('배송지 삭제에 실패했습니다.');
      }
    }
  };

  // 배송지 저장 핸들러
  const handleSaveAddress = async () => {
    if (!formData.name || !formData.mobile || !formData.postcode || !formData.address1) {
      alert('필수 항목을 모두 입력해주세요.');
      return;
    }

    // 연락처 유효성 검증
    if (!isValidPhoneNumber(formData.mobile)) {
      alert('연락처가 유효하지 않습니다. 올바른 연락처를 입력해주세요.');
      return;
    }

    // 보조 연락처가 있는 경우에만 검증
    if (formData.phone && !isValidPhoneNumber(formData.phone)) {
      alert('보조 연락처가 유효하지 않습니다. 올바른 연락처를 입력해주세요.');
      return;
    }

    try {
      if (isEditingAddress) {
        // 수정 모드
        await props.ShippingAddressesActions.updateShippingAddress(editingAddressId, formData);
      } else {
        // 추가 모드
        await props.ShippingAddressesActions.createShippingAddress(formData);
      }

      // 목록 새로고침
      await loadShippingAddresses();

      // 폼 초기화
      setFormData({
        label: '',
        name: '',
        mobile: '',
        phone: '',
        postcode: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        country: '',
        company: '',
      });
      setIsEditingAddress(false);
      setIsAddingNewAddress(false);
      setEditingAddressId(null);
    } catch (error) {
      alert('배송지 저장에 실패했습니다.');
    }
  };

  // 폼 입력 핸들러
  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // 우편번호 찾기 핸들러
  const handleFindAddress = () => {
    if (window.daum && window.daum.Postcode) {
      new window.daum.Postcode({
        oncomplete: data => {
          handleFormChange('country', 'KR');
          handleFormChange('city', data.sigungu);
          handleFormChange('state', data.sido);
          handleFormChange('postcode', data.zonecode);
          handleFormChange('address1', data.address);
        },
      }).open();
    } else {
      alert('우편번호 서비스를 불러올 수 없습니다.');
    }
  };

  return (
    <div className="CheckoutTemplate">
      <div className="checkout order-checkout">
        <div className="checkout-process">
          <div className="cart-items">
            <h2 className="with-back-button">
              <Link className="back-to-cart" to={'/cart'}>
                <MdArrowBack />
              </Link>
              주문서
            </h2>

            <div className="checkout-items-list">
              {props.cart.items.map((item, index) => (
                <div key={item.id} className="checkout-item-card">
                  {/* 첫번째 줄: 타이틀 */}
                  <div className="checkout-item-title">
                    <h3 className="product-name">{item.product.name}</h3>
                  </div>

                  {/* 두번째 줄: 카탈로그 번호, 단위 */}
                  <div className="checkout-item-meta">
                    {item.product_price && item.product_price.part_number && (
                      <div className="meta-item">
                        <span className="meta-label">카탈로그 번호:</span>
                        <span className="meta-value">{item.product_price.part_number}</span>
                      </div>
                    )}
                    {item.product_price && item.product_price.unit && (
                      <div className="meta-item">
                        <span className="meta-label">단위:</span>
                        <span className="meta-value">{item.product_price.unit}</span>
                      </div>
                    )}
                  </div>

                  {/* 세번째 줄: 이미지 | 설명 | 수량 */}
                  <div className="checkout-item-main">
                    <div className="item-image">
                      <img
                        src={imageURL(item.product.thumbnail, 120, 120)}
                        alt={item.product.name}
                        className="product-thumbnail"
                      />
                    </div>

                    <div className="item-description">
                      {item.product_price && item.product_price.description ? (
                        <span>{item.product_price.description}</span>
                      ) : (
                        <span className="no-description">설명 없음</span>
                      )}
                    </div>

                    <div className="item-quantity">
                      <span className="quantity-label">수량:</span>
                      <span className="quantity-value">{item.quantity}</span>
                    </div>
                  </div>

                  {/* 네번째 줄: 단가 | 가격 */}
                  <div className="checkout-item-pricing">
                    <div className="unit-price">
                      <span className="price-label">단가:</span>
                      {item.price ? (
                        <div className="price-info">
                          {item.price.sale.raw !== item.price.original.raw && (
                            <span className="original-price">
                              {numberWithCommas(item.price.original.raw)}원
                            </span>
                          )}
                          <span className="sale-price">
                            {numberWithCommas(item.price.sale.raw)}원
                          </span>
                        </div>
                      ) : (
                        <span className="sale-price">
                          {numberWithCommas(item.product_price.price)}원
                        </span>
                      )}
                    </div>

                    <div className="total-price">
                      <span className="price-label">가격:</span>
                      <div className="price-info">
                        {item.total.price.sale.raw !== item.total.price.original.raw && (
                          <span className="original-price">
                            {numberWithCommas(item.total.price.original.raw)}원
                          </span>
                        )}
                        <span className="sale-price">
                          {numberWithCommas(item.total.price.sale.raw)}원
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="recipient-info form">
            <div className="orderer-info-header">
              <h2>주문자 정보</h2>

              {/* 주문자 정보 타입 선택 라디오 버튼 */}
              <div className="orderer-info-type-selector">
                <label className="radio-option" htmlFor="ordererInfoType-myInfo">
                  <input
                    id="ordererInfoType-myInfo"
                    type="radio"
                    name="ordererInfoType"
                    value="myInfo"
                    checked={props.ordererInfoType === 'myInfo'}
                    onChange={props.onOrdererInfoTypeChange}
                  />
                  <span className="radio-label">내정보</span>
                </label>
                <label className="radio-option" htmlFor="ordererInfoType-changeInfo">
                  <input
                    id="ordererInfoType-changeInfo"
                    type="radio"
                    name="ordererInfoType"
                    value="changeInfo"
                    checked={props.ordererInfoType === 'changeInfo'}
                    onChange={props.onOrdererInfoTypeChange}
                  />
                  <span className="radio-label">주문자변경</span>
                </label>
              </div>
            </div>

            <div className="address-recipient">
              {/* name.full 필드 */}
              <div className="customer-name-field">
                <label htmlFor="addressNameFull">
                  <span>주문자</span>
                  <input
                    id="addressNameFull"
                    type="text"
                    name="addressNameFull"
                    value={props.addressNameFull}
                    placeholder="성함을 입력해주세요"
                    required
                    onChange={props.handleChange}
                  />
                </label>
              </div>

              {/* mobile 필드 */}
              <div className="customer-mobile-field">
                <label htmlFor="addressMobile">
                  <span>연락처</span>
                  <input
                    id="addressMobile"
                    type="tel"
                    name="addressMobile"
                    value={props.addressMobile}
                    placeholder="연락처를 입력해주세요"
                    required
                    onChange={e => {
                      const formattedValue = formatPhoneNumber(e.target.value);
                      const syntheticEvent = {
                        target: {
                          name: e.target.name,
                          value: formattedValue,
                        },
                      };
                      props.handleChange(syntheticEvent);
                    }}
                  />
                </label>
              </div>

              {/* email 필드 */}
              <div className="customer-email-field">
                <label htmlFor="addressEmail">
                  <span>이메일</span>
                  <input
                    id="addressEmail"
                    type="email"
                    name="addressEmail"
                    value={props.addressEmail}
                    placeholder="email@cacheby.com"
                    required
                    onChange={props.handleChange}
                  />
                </label>
              </div>

              {/* phone 필드 */}
              <div className="customer-phone-field">
                <label htmlFor="addressPhone">
                  <span>사업자등록번호</span>
                  <input
                    id="addressPhone"
                    type="text"
                    name="addressPhone"
                    value={props.addressPhone}
                    placeholder="사업자등록번호를 입력해주세요"
                    onChange={e => {
                      const formattedValue = formatBusinessNumber(e.target.value);
                      const syntheticEvent = {
                        target: {
                          name: e.target.name,
                          value: formattedValue,
                        },
                      };
                      props.handleChange(syntheticEvent);
                    }}
                  />
                </label>
              </div>
              <div className="orderer-info-box">
                <div className="info-item">
                  <span className="info-text">주문내역은 이메일로 안내 받을 수 있어요.</span>
                </div>
                <div className="info-item">
                  <span className="info-text">
                    회원정보 변경은
                    <Link className="profile-link" to={'/settings'}>
                      &quot;프로필 &gt; 회원정보수정&quot;
                    </Link>
                    에서 가능해요.
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="address form">
            <div className="shipping-info-header">
              <div className="shipping-info-single-row">
                <div className="shipping-info-title">
                  <h2>배송지 정보</h2>

                  {/* 배송지 정보 타입 선택 라디오 버튼 */}
                  <div className="shipping-info-type-selector">
                    <label className="radio-option" htmlFor="shippingInfoType-recent">
                      <input
                        id="shippingInfoType-recent"
                        type="radio"
                        name="shippingInfoType"
                        value="recentAddress"
                        checked={props.shippingInfoType === 'recentAddress'}
                        onChange={props.onShippingInfoTypeChange}
                      />
                      <span className="radio-label">최근 배송지</span>
                    </label>
                    <label className="radio-option" htmlFor="shippingInfoType-new">
                      <input
                        id="shippingInfoType-new"
                        type="radio"
                        name="shippingInfoType"
                        value="newAddress"
                        checked={props.shippingInfoType === 'newAddress'}
                        onChange={props.onShippingInfoTypeChange}
                      />
                      <span className="radio-label">새 배송지</span>
                    </label>
                  </div>
                </div>

                {props.isAuthenticated && (
                  <button
                    className="shipping-address-list-btn"
                    onClick={() => setShowAddressModal(true)}
                    type="button"
                  >
                    배송지 목록
                  </button>
                )}
              </div>
            </div>

            <div className="address-location">
              {/* 받는분과 연락처를 각각 한 줄씩 배치 */}
              <div className="address-recipient">
                <label htmlFor="addressShippingNameFull">
                  <span>받는분</span>
                  <input
                    id="addressShippingNameFull"
                    type="text"
                    name="addressShippingNameFull"
                    value={props.addressShippingNameFull}
                    placeholder="성함을 입력해주세요"
                    onChange={props.handleChange}
                    required
                  />
                </label>
              </div>

              <div className="address-phone">
                <label htmlFor="addressShippingMobile">
                  <span>연락처</span>
                  <input
                    id="addressShippingMobile"
                    type="tel"
                    name="addressShippingMobile"
                    value={props.addressShippingMobile}
                    placeholder="연락처를 입력해주세요"
                    onChange={e => {
                      const formattedValue = formatPhoneNumber(e.target.value);
                      const syntheticEvent = {
                        target: {
                          name: e.target.name,
                          value: formattedValue,
                        },
                      };
                      props.handleChange(syntheticEvent);
                    }}
                    required
                  />
                </label>
              </div>

              {/* 주소 섹션 - 라벨과 첫 번째 필드를 같은 줄에 배치 */}
              <div className="address-section">
                {/* 주소 라벨과 우편번호 찾기를 같은 줄에 배치 */}
                <div className="address-label-with-first-field">
                  <span className="address-label-text">주소</span>

                  {/* 우편번호 찾기 버튼과 우편번호를 한 줄에 배치 */}
                  <div className="postcode-info">
                    <div className="postcode-search">
                      <Button
                        className="search-address"
                        variant="outline"
                        color="blue"
                        onClick={props.onFindAddress}
                      >
                        우편번호 찾기
                      </Button>
                    </div>

                    <div className="address-postcode">
                      <input
                        id="addressPostCode"
                        type="text"
                        name="addressPostCode"
                        value={props.addressPostCode}
                        placeholder="우편번호"
                        readOnly
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="address-fields">
                  <div className="address-address1">
                    <input
                      id="addressAddress1"
                      type="text"
                      name="addressAddress1"
                      value={props.addressAddress1}
                      placeholder="주소"
                      readOnly
                      required
                    />
                  </div>

                  <div className="address-address2">
                    <input
                      id="addressAddress2"
                      type="text"
                      name="addressAddress2"
                      value={props.addressAddress2}
                      placeholder="상세주소를 입력해주세요"
                      onChange={props.handleChange}
                      required
                    />
                  </div>
                </div>
              </div>

              <ShippingMemoDropdown value={props.request} onChange={props.handleChange} />

              {/* {props.isAuthenticated && (
              <div className="save-primary-address">
                <label htmlFor="save-primary-address-agreement">
                  <input type="checkbox" checked id="save-primary-address-agreement" />
                  이 주소를 기본 주소로 저장
                </label>
              </div>
            )} */}
            </div>
          </div>

          <div className="order-payment">
            <div className="payment-details form">
              <h2>결제수단</h2>

              <div id="payment-widget" />
              {/* <div id="agreement" /> */}
            </div>
          </div>

          {/* 발급수단 섹션 */}
          {(props.selectedPaymentType === 'BANK_TRANSFER' ||
            props.selectedPaymentType === 'POST_TRANSFER') && (
            <div className="document-issuance">
              <div className="document-details form">
                <div className="document-header">
                  <div className="document-info-single-row">
                    <div className="document-info-title">
                      <h2>발급수단</h2>

                      {/* 무통장 입금: 세금계산서만 */}
                      {props.selectedPaymentType === 'BANK_TRANSFER' && (
                        <div className="document-type-selector">
                          <label className="radio-option">
                            <input
                              type="radio"
                              name="documentType"
                              value="TAX_INVOICE"
                              checked={props.documentaryEvidenceInfo.documentType === 'TAX_INVOICE'}
                              onChange={props.onDocumentTypeChange}
                            />
                            <span className="radio-label">세금계산서</span>
                          </label>
                        </div>
                      )}

                      {/* 후불 결제: 세금계산서, 신용카드 */}
                      {props.selectedPaymentType === 'POST_TRANSFER' && (
                        <div className="document-type-selector">
                          <label className="radio-option">
                            <input
                              type="radio"
                              name="documentType"
                              value="TAX_INVOICE"
                              checked={props.documentaryEvidenceInfo.documentType === 'TAX_INVOICE'}
                              onChange={props.onDocumentTypeChange}
                            />
                            <span className="radio-label">세금계산서</span>
                          </label>
                          <label className="radio-option">
                            <input
                              type="radio"
                              name="documentType"
                              value="CREDIT_CARD"
                              checked={props.documentaryEvidenceInfo.documentType === 'CREDIT_CARD'}
                              onChange={props.onDocumentTypeChange}
                            />
                            <span className="radio-label">신용/연구비카드</span>
                          </label>
                        </div>
                      )}
                    </div>

                    {props.isAuthenticated && (
                      <button
                        className="document-list-btn"
                        onClick={() => {
                          loadTaxInvoices();
                          setShowDocumentModal(true);
                        }}
                        type="button"
                      >
                        발급목록
                      </button>
                    )}
                  </div>
                </div>

                {/* 세금계산서 선택 시만 정보 입력 */}
                {props.documentaryEvidenceInfo.documentType === 'TAX_INVOICE' && (
                  <div className="document-form">
                    {/* 담당자명 | 세금계산서 이메일 */}
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="documentName">
                          <span>담당자명</span>
                          <input
                            id="documentName"
                            type="text"
                            value={props.documentaryEvidenceInfo.name}
                            placeholder="최영진"
                            onChange={e => props.onDocumentInfoChange('name', e.target.value)}
                          />
                        </label>
                      </div>
                      <div className="form-group">
                        <label htmlFor="documentEmail">
                          <span>세금계산서 이메일</span>
                          <input
                            id="documentEmail"
                            type="email"
                            value={props.documentaryEvidenceInfo.email}
                            placeholder="info@cacheby.com"
                            onChange={e => props.onDocumentInfoChange('email', e.target.value)}
                          />
                        </label>
                      </div>
                    </div>

                    {/* 담당자 연락처 | 담당자 보조 연락처 */}
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="documentContact">
                          <span>담당자 연락처</span>
                          <input
                            id="documentContact"
                            type="tel"
                            value={props.documentaryEvidenceInfo.contact}
                            placeholder="- 없이 숫자만 입력해주세요"
                            onChange={e => {
                              const formattedValue = formatPhoneNumber(e.target.value);
                              props.onDocumentInfoChange('contact', formattedValue);
                            }}
                          />
                        </label>
                      </div>
                      <div className="form-group">
                        <label htmlFor="documentSubContact">
                          <span>
                            담당자 보조 연락처 <span className="optional-text">(선택)</span>
                          </span>
                          <input
                            id="documentSubContact"
                            type="tel"
                            value={props.documentaryEvidenceInfo.sub_contact}
                            placeholder="- 없이 숫자만 입력해주세요"
                            onChange={e => {
                              const formattedValue = formatPhoneNumber(e.target.value);
                              props.onDocumentInfoChange('sub_contact', formattedValue);
                            }}
                          />
                        </label>
                      </div>
                    </div>

                    {/* 상호/법인명 | 사업자등록번호 */}
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="documentCompanyName">
                          <span>상호/법인명</span>
                          <input
                            id="documentCompanyName"
                            type="text"
                            value={props.documentaryEvidenceInfo.company_name}
                            placeholder="주식회사 데이터씨"
                            onChange={e =>
                              props.onDocumentInfoChange('company_name', e.target.value)
                            }
                          />
                        </label>
                      </div>
                      <div className="form-group">
                        <label htmlFor="documentBusinessNo">
                          <span>사업자등록번호</span>
                          <input
                            id="documentBusinessNo"
                            type="text"
                            value={props.documentaryEvidenceInfo.business_no}
                            placeholder="4648100805"
                            onChange={e => {
                              const formattedValue = formatBusinessNumber(e.target.value);
                              props.onDocumentInfoChange('business_no', formattedValue);
                            }}
                          />
                        </label>
                      </div>
                    </div>

                    {/* 대표자명 | 사업장 주소 */}
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="documentRepresentativeName">
                          <span>대표자명</span>
                          <input
                            id="documentRepresentativeName"
                            type="text"
                            value={props.documentaryEvidenceInfo.representative_name}
                            placeholder="최영진"
                            onChange={e =>
                              props.onDocumentInfoChange('representative_name', e.target.value)
                            }
                          />
                        </label>
                      </div>
                      <div className="form-group">
                        <label htmlFor="documentAddress">
                          <span>사업장 주소</span>
                          <input
                            id="documentAddress"
                            type="text"
                            value={props.documentaryEvidenceInfo.business_address}
                            placeholder="서울시 서초구 동광로 1길 52, 1층"
                            onChange={e =>
                              props.onDocumentInfoChange('business_address', e.target.value)
                            }
                          />
                        </label>
                      </div>
                    </div>

                    {/* 업태 | 업종 */}
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="documentBusinessType">
                          <span>
                            업태 <span className="optional-text">(선택)</span>
                          </span>
                          <input
                            id="documentBusinessType"
                            type="text"
                            value={props.documentaryEvidenceInfo.business_type}
                            placeholder="도매 및 소매업"
                            onChange={e =>
                              props.onDocumentInfoChange('business_type', e.target.value)
                            }
                          />
                        </label>
                      </div>
                      <div className="form-group">
                        <label htmlFor="documentBusinessItem">
                          <span>
                            업종 <span className="optional-text">(선택)</span>
                          </span>
                          <input
                            id="documentBusinessItem"
                            type="text"
                            value={props.documentaryEvidenceInfo.business_item}
                            placeholder="전자상거래"
                            onChange={e =>
                              props.onDocumentInfoChange('business_item', e.target.value)
                            }
                          />
                        </label>
                      </div>
                    </div>

                    <div className="document-agreement">
                      <div className="issuance-timing-selector">
                        <label htmlFor="issuanceAfterDelivery">
                          <input
                            type="radio"
                            id="issuanceAfterDelivery"
                            name="preIssuanceAgreement"
                            checked={!documentaryEvidenceInfo.preIssuanceAgreement}
                            onChange={() =>
                              props.onDocumentInfoChange('preIssuanceAgreement', false)
                            }
                          />
                          <span className="radio-label">물건 받으면 세금계산서 발급해주세요.</span>
                        </label>
                        <label htmlFor="issuanceImmediate">
                          <input
                            type="radio"
                            id="issuanceImmediate"
                            name="preIssuanceAgreement"
                            checked={documentaryEvidenceInfo.preIssuanceAgreement}
                            onChange={() =>
                              props.onDocumentInfoChange('preIssuanceAgreement', true)
                            }
                          />
                          <span className="radio-label">세금계산서 먼저 발급해주세요.</span>
                        </label>
                      </div>
                    </div>

                    <div className="document-info-box">
                      <MdInfoOutline className="info-icon" />
                      <div className="info-content">
                        <div className="info-text">주문취소 시 취소 세금계산서가 발행됩니다</div>
                        <div className="info-text">
                          발행일 기준 익월 10일 이후에는 세금계산서 수정발급이 불가합니다. 기재된
                          내용이 정확한지 꼭 확인 부탁드립니다.
                        </div>
                        <div className="info-text">
                          현금영수증 발급이 필요할 경우 가상계좌로 결제해 주시기 바랍니다.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="total">
          <h2>주문 요약</h2>

          <div className="total-details">
            <table>
              <tbody>
                <tr className="item-total">
                  <th>상품 합계</th>
                  <td>
                    <span className="cart-items-sale-price">
                      {numberWithCommas(props.cart.total.price.sale.raw)}원
                    </span>
                    {props.cart.total.price.sale.raw !== props.cart.total.price.original.raw && (
                      <span className="cart-items-compare-price">
                        {numberWithCommas(props.cart.total.price.original.raw)}원
                      </span>
                    )}
                    {props.cart.total.price.sale.raw !== props.cart.total.price.original.raw && (
                      <span className="cart-items-discounted">
                        (-{numberWithCommas(props.cart.total.discounted.raw)}원)
                      </span>
                    )}
                  </td>
                </tr>
                <tr className="shipping-total">
                  <th>배송비 합계</th>
                  <td>{numberWithCommas(props.cart.total.shipping.fee.sale.raw)}원</td>
                </tr>
                <tr className="tax-total">
                  <th>부가세</th>
                  <td>{numberWithCommas(props.cart.total.taxed.raw)}원</td>
                </tr>
                <tr className="order-total">
                  <th>총계</th>
                  <td>{numberWithCommas(props.cart.total.amount.raw)}원</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="checkout-agreements">
            <div key="terms" className={`checkout-terms`} data-agreement-scope="terms">
              <label htmlFor={`checkout-terms-agreement`}>
                <input
                  type="checkbox"
                  required
                  id={`checkout-terms-agreement`}
                  checked={props.checkoutTerms}
                  onChange={props.onCheckoutTerms}
                />
                {statusTrans(camelCase(`agreeWith_terms`))}
              </label>
              <a className="view-checkout-scope" href={`/policy/terms`} target="_blank">
                {statusTrans(camelCase(`view_terms`))}
              </a>
            </div>
            <div key="privacy" className={`checkout-privacy`} data-agreement-scope="privacy">
              <label htmlFor={`checkout-privacy-agreement`}>
                <input
                  type="checkbox"
                  required
                  id={`checkout-privacy-agreement`}
                  checked={props.checkoutPrivacy}
                  onChange={props.onCheckoutPrivacy}
                />
                {statusTrans(camelCase(`agreeWith_privacy`))}
              </label>
              <a className="view-checkout-scope" href={`/policy/privacy`} target="_blank">
                {statusTrans(camelCase(`view_privacy`))}
              </a>
            </div>

            <div className="checkout-all" data-agreement-scope="all">
              <label htmlFor="checkout-all-agreement">
                <input
                  type="checkbox"
                  id="checkout-all-agreement"
                  checked={props.checkoutAll}
                  onChange={props.onCheckoutAll}
                />
                전체 동의
              </label>
            </div>
          </div>

          <Button className="proceed-checkout button" onClick={props.onCheckOut}>
            결제하기
          </Button>

          {/* <ul className="order-summary-tips">
          <li>대한민국 원 (KRW)</li>
          <li>부가세는 가격에 미포함 되어있습니다.</li>
          <li>배송비와 부가세는 배송지 정보 설정 후에 계산됩니다.</li>
        </ul> */}
        </div>
      </div>

      {/* 발급목록 모달 */}
      {showDocumentModal && (
        <div className="address-modal-overlay" onClick={() => setShowDocumentModal(false)}>
          <div className="address-modal" onClick={e => e.stopPropagation()}>
            <div className="address-modal-header">
              <h2>세금계산서 발급목록</h2>
              <button
                className="modal-close-btn"
                onClick={() => setShowDocumentModal(false)}
                type="button"
              >
                ✕
              </button>
            </div>

            <div className="address-modal-content">
              {taxInvoiceList.length > 0 ? (
                taxInvoiceList.map((doc, index) => (
                  <div key={index} className="document-item">
                    <div className="document-info">
                      <div className="document-row">
                        <span className="document-label">담당자명</span>
                        <span className="document-value">{doc.name}</span>
                      </div>
                      <div className="document-row">
                        <span className="document-label">세금계산서 이메일</span>
                        <span className="document-value">{doc.email}</span>
                      </div>
                      <div className="document-row">
                        <span className="document-label">담당자 연락처</span>
                        <span className="document-value">{doc.contact}</span>
                      </div>
                      <div className="document-row">
                        <span className="document-label">담당자 보조 연락처</span>
                        <span className="document-value">{doc.sub_contact || '-'}</span>
                      </div>
                      <div className="document-row">
                        <span className="document-label">상호/법인명</span>
                        <span className="document-value">{doc.company_name}</span>
                      </div>
                      <div className="document-row">
                        <span className="document-label">사업자등록번호</span>
                        <span className="document-value">{doc.business_no}</span>
                      </div>
                      <div className="document-row">
                        <span className="document-label">대표자명</span>
                        <span className="document-value">{doc.representative_name}</span>
                      </div>
                      <div className="document-row">
                        <span className="document-label">사업장 주소</span>
                        <span className="document-value">{doc.business_address}</span>
                      </div>
                      <div className="document-row">
                        <span className="document-label">업태</span>
                        <span className="document-value">{doc.business_type || '-'}</span>
                      </div>
                      <div className="document-row">
                        <span className="document-label">업종</span>
                        <span className="document-value">{doc.business_item || '-'}</span>
                      </div>
                    </div>
                    <div className="document-actions">
                      <button
                        className="address-btn select-btn"
                        onClick={() => {
                          // 필요한 필드만 추출
                          const fieldsToUpdate = {
                            name: doc.name,
                            contact: doc.contact,
                            sub_contact: doc.sub_contact,
                            email: doc.email,
                            business_no: doc.business_no,
                            tax_registration_id: doc.tax_registration_id,
                            business_item: doc.business_item,
                            business_type: doc.business_type,
                            company_name: doc.company_name,
                            representative_name: doc.representative_name,
                            business_address: doc.business_address,
                          };

                          // 한 번에 모든 필드 업데이트
                          props.onDocumentInfoBulkUpdate(fieldsToUpdate);
                          setShowDocumentModal(false);
                        }}
                        type="button"
                      >
                        선택
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-address-list">
                  <p>저장된 발급정보가 없습니다.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 배송지 관리 모달 */}
      {showAddressModal && (
        <div className="address-modal-overlay" onClick={() => setShowAddressModal(false)}>
          <div className="address-modal" onClick={e => e.stopPropagation()}>
            <div className="address-modal-header">
              <h2>{isAddingNewAddress ? '배송지 추가' : '배송지 목록'}</h2>
              <button
                className="modal-close-btn"
                onClick={() => setShowAddressModal(false)}
                type="button"
              >
                ✕
              </button>
            </div>

            <div className="address-modal-content">
              {!isAddingNewAddress && !isEditingAddress ? (
                // 배송지 목록 표시
                <div className="address-list">
                  {addressList.length > 0 ? (
                    addressList.map(address => (
                      <div key={address.id} className="address-item">
                        <div className="address-info">
                          {address.label && <div className="address-label">{address.label}</div>}
                          <div className="address-name">
                            <strong>{address.name}</strong>
                            <span className="address-phone">{address.mobile}</span>
                          </div>
                          <div className="address-detail">
                            [{address.postcode}] {address.address1}
                          </div>
                          {address.address2 && (
                            <div className="address-detail2">{address.address2}</div>
                          )}
                        </div>
                        <div className="address-actions">
                          <button
                            className="address-btn select-btn"
                            onClick={() => handleSelectAddress(address)}
                            type="button"
                          >
                            선택
                          </button>
                          <button
                            className="address-btn edit-btn"
                            onClick={() => handleEditAddress(address)}
                            type="button"
                          >
                            수정
                          </button>
                          <button
                            className="address-btn delete-btn"
                            onClick={() => handleDeleteAddress(address.id)}
                            type="button"
                          >
                            삭제
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="empty-address-list">
                      <p>저장된 배송지가 없습니다.</p>
                    </div>
                  )}

                  <button
                    className="add-new-address-btn"
                    onClick={() => setIsAddingNewAddress(true)}
                    type="button"
                  >
                    + 새 배송지 추가
                  </button>
                </div>
              ) : (
                // 배송지 추가/수정 폼
                <div className="address-form">
                  {/* <h3>{isEditingAddress ? '배송지 수정' : '배송지 추가'}</h3> */}

                  <div className="form-group">
                    <label>배송지명 (선택)</label>
                    <input
                      type="text"
                      placeholder="배송지명을 입력해주세요"
                      className="form-input"
                      value={formData.label}
                      onChange={e => handleFormChange('label', e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label>이름</label>
                    <input
                      type="text"
                      placeholder="성함을 입력해주세요"
                      className="form-input"
                      value={formData.name}
                      onChange={e => handleFormChange('name', e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label>연락처</label>
                    <input
                      type="tel"
                      placeholder="연락처를 입력해주세요"
                      className="form-input"
                      value={formData.mobile}
                      onChange={e => handleFormChange('mobile', formatPhoneNumber(e.target.value))}
                    />
                  </div>

                  <div className="form-group">
                    <label>보조 연락처 (선택)</label>
                    <input
                      type="tel"
                      placeholder="보조 연락처를 입력해주세요"
                      className="form-input"
                      value={formData.phone}
                      onChange={e => handleFormChange('phone', formatPhoneNumber(e.target.value))}
                    />
                  </div>

                  <div className="form-group">
                    <label>주소</label>
                    <div className="address-input-group">
                      <button className="postcode-btn" type="button" onClick={handleFindAddress}>
                        우편번호 찾기
                      </button>
                      <input
                        type="text"
                        placeholder=""
                        className="form-input postcode-input"
                        value={formData.postcode}
                        readOnly
                      />
                    </div>
                    <input
                      type="text"
                      placeholder=""
                      className="form-input address-input"
                      value={formData.address1}
                      readOnly
                    />
                    <input
                      type="text"
                      placeholder="상세주소 입력"
                      className="form-input"
                      value={formData.address2}
                      onChange={e => handleFormChange('address2', e.target.value)}
                    />
                  </div>

                  <div className="form-actions">
                    <button
                      className="cancel-btn"
                      onClick={() => {
                        setIsAddingNewAddress(false);
                        setIsEditingAddress(false);
                        setEditingAddressId(null);
                      }}
                      type="button"
                    >
                      취소
                    </button>
                    <button className="confirm-btn" onClick={handleSaveAddress} type="button">
                      완료
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  shippingAddresses: state.shippingAddresses,
  user: state.user,
});

const mapDispatchToProps = dispatch => ({
  ShippingAddressesActions: bindActionCreators(shippingAddressesActions, dispatch),
  UserActions: bindActionCreators(userActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(CheckoutTemplate);



// WEBPACK FOOTER //
// ./src/components/checkout/CheckoutTemplate/CheckoutTemplate.js